// Engraving renderer: one scene pass fills a G-buffer (lit, albedo, mask, normal + depth) that one post
// shader composes. Ported from prototypes/match/js/engrave.js as-is — this is not the place to redesign
// anything. Every scene mesh MUST be created through mk() or adoptMesh(), or its G-buffer outputs are
// undefined (the post shader will read garbage albedo/mask/normal for it).
import * as THREE from 'three';

export const TEAM: Record<'a' | 'b', number> = { a: 0xd9483b, b: 0x3a6fd9 };
// unit: hero parts without team color — the shader gives them a tonal wash so they stand off the ground
// prop: trees, ruins, buildings — coloured only inside the team's vision (fog of war stays a two-tone print)
// ghost: outline only (a dodging hero drawn as bare contour on paper)
// shade: Слиться as her own team sees her — contour over paper with sparse hatching, a sketch of someone not there
// foliage sits just inside the prop range: it takes the same fog wash, but the post shader thins its hatching
export const MASK: Record<string, number> = { none: 0x000000, prop: 0x141414, foliage: 0x181818, unit: 0x404040, a: 0xff0000, b: 0x0000ff, fx: 0x00ff00, ghost: 0x000080, shade: 0x001f80 };

// lit: the rendered material, owned by this mesh — fx only (the fx colour is written per mesh). Every other
// mesh renders a lit material shared by all meshes that look the same (see sharedLit) and owns none.
type Mats = { albedo: THREE.MeshBasicMaterial; lit?: THREE.Material; mask: THREE.MeshBasicMaterial };
type GMesh = (THREE.Mesh | THREE.InstancedMesh) & { userData: { mats: Mats } & Record<string, any> };

const meshes: GMesh[] = [];

/* One lit material per (colour, side, mask code, mesh kind) instead of one per mesh: a busy match went from
   ~500–570 materials to ~80–125, so consecutive draws skip the uniform re-upload and state switch. The mask
   code is baked into the shared material's uMask; a mesh whose mask changes (ghost, dying) is moved onto a
   private copy by syncMasks() and back when the mask returns. Kind is in the key because three.js rebuilds
   the program lookup every time one material alternates between plain, instanced and skinned objects.
   Shared materials live for the page: release() never disposes them. */
const litCache = new Map<string, THREE.MeshLambertMaterial>();
function sharedLit(color: THREE.Color, side: THREE.Side, mask: THREE.Color, kind: string) {
  const key = `${color.r},${color.g},${color.b}|${side}|${mask.r},${mask.g},${mask.b}|${kind}`;
  let mat = litCache.get(key);
  if (!mat) {
    mat = gbuffer(new THREE.MeshLambertMaterial({ color, side })) as THREE.MeshLambertMaterial;
    mat.userData.uMask.value.copy(mask);
    litCache.set(key, mat);
  }
  return mat;
}

export type MkOpts = { team?: string; fx?: boolean; cast?: boolean; count?: number; double?: boolean };

export function mk(geo: THREE.BufferGeometry, color: number, { team = 'none', fx = false, cast = true, count = 0, double = false }: MkOpts = {}): GMesh {
  const c = new THREE.Color(color);
  const side = double ? THREE.DoubleSide : THREE.FrontSide;
  const albedo = new THREE.MeshBasicMaterial({ color: c, side });
  const mask = new THREE.MeshBasicMaterial({ color: fx ? MASK.fx : MASK[team], side });
  const lit = fx ? gbuffer(albedo) : sharedLit(c, side, mask.color, count ? 'instanced' : 'mesh');
  const m = (count ? new THREE.InstancedMesh(geo, lit, count) : new THREE.Mesh(geo, lit)) as GMesh;
  m.userData.mats = fx ? { albedo, lit, mask } : { albedo, mask };
  if (!fx) m.userData.sharedLit = lit;
  m.castShadow = cast && !fx;
  m.receiveShadow = !fx;
  meshes.push(m);
  return m;
}

/* A glTF mesh joins the passes; materials named team_* take the team color. */
export function adoptMesh(mesh: THREE.Mesh, team: 'a' | 'b') {
  const src = mesh.material as THREE.MeshStandardMaterial, accent = /^team/.test(src.name);
  const color = accent ? new THREE.Color(TEAM[team]) : src.color.clone().convertLinearToSRGB();
  const albedo = new THREE.MeshBasicMaterial({ color, side: src.side });
  const mask = new THREE.MeshBasicMaterial({ color: MASK[accent ? team : 'unit'], side: src.side });
  const lit = sharedLit(color, src.side, mask.color, (mesh as THREE.SkinnedMesh).isSkinnedMesh ? 'skinned' : 'mesh');
  mesh.material = lit;
  (mesh as GMesh).userData.mats = { albedo, mask };
  mesh.userData.sharedLit = lit;
  mesh.castShadow = mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  meshes.push(mesh as GMesh);
}

export function forget(root: THREE.Object3D) {
  root.traverse(o => { const k = meshes.indexOf(o as GMesh); if (k >= 0) meshes.splice(k, 1); });
}

/* forget() plus freeing the GPU side: every material mk()/adoptMesh()/setGhost()/setDying()/syncMasks() made
   for the tree (never the shared lit ones), bone textures, and — unless the caller shares them (cached glTF clones) — the geometries. */
export function release(root: THREE.Object3D, { geometry = true }: { geometry?: boolean } = {}) {
  forget(root);
  root.traverse(o => {
    const u = o.userData;
    if (u.mats) { const m = u.mats as Mats; m.albedo.dispose(); m.lit?.dispose(); m.mask.dispose(); }
    u.ownLit?.dispose(); u.maskOrig?.dispose(); u.maskGhost?.dispose(); u.maskShade?.dispose(); u.maskSwap?.dispose(); u.dyingMask?.dispose();
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) (o as THREE.SkinnedMesh).skeleton.dispose();
    if (geometry && (o as THREE.Mesh).isMesh) (o as THREE.Mesh).geometry.dispose();
  });
}

/** Size of the list syncMasks() walks every frame — for the leak tests. */
export const trackedMeshCount = () => meshes.length;

/* Swap a rig's mask pass to the ghost code (and back). */
export function setGhost(root: THREE.Object3D, on: boolean) { setMaskLook(root, on ? 'ghost' : 'normal'); }

export type MaskLook = 'normal' | 'ghost' | 'shade';
/* Which mask pass a rig renders with: its own, the ghost contour or Слиться's shade. `swapTeam` repaints its
   team-coloured parts in the other team's colour (Наваждение, delusion.ts); ghost and shade carry no team
   colour to swap. Not for a dying rig: setDying() keeps its own copy of whatever mask this left. */
export function setMaskLook(root: THREE.Object3D, look: MaskLook, swapTeam = false) {
  root.traverse(o => {
    const mats = o.userData && o.userData.mats as Mats | undefined;
    if (!mats) return;
    const u = o.userData;
    if (!u.maskOrig) {
      u.maskOrig = mats.mask;
      u.maskGhost = new THREE.MeshBasicMaterial({ color: MASK.ghost, side: mats.mask.side });
    }
    const orig = u.maskOrig as THREE.MeshBasicMaterial, side = orig.side;
    if (look === 'ghost') mats.mask = u.maskGhost;
    else if (look === 'shade') mats.mask = u.maskShade ??= new THREE.MeshBasicMaterial({ color: MASK.shade, side });
    else if (swapTeam && (orig.color.getHex() === MASK.a || orig.color.getHex() === MASK.b)) {
      mats.mask = u.maskSwap ??= new THREE.MeshBasicMaterial({ color: orig.color.getHex() === MASK.a ? MASK.b : MASK.a, side });
    } else mats.mask = orig;
  });
}

/* Dying: every mask of the rig switches to one shared material whose blue channel carries the progress. */
export function setDying(root: THREE.Object3D, p: number | null) {
  let mat: THREE.MeshBasicMaterial | null = root.userData.dyingMask ?? null;
  if (p === null) {
    if (!mat) return;
    root.traverse(o => { if (o.userData.maskDyingOrig) { (o.userData.mats as Mats).mask = o.userData.maskDyingOrig; delete o.userData.maskDyingOrig; } });
    root.userData.dyingMask = null;
    return;
  }
  if (!mat) {
    mat = root.userData.dyingMask = new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide });
    root.traverse(o => {
      const mats = o.userData && o.userData.mats as Mats | undefined;
      if (!mats || o.userData.maskDyingOrig) return;
      o.userData.maskDyingOrig = mats.mask;
      mats.mask = mat as THREE.MeshBasicMaterial;
    });
  }
  mat.color.setRGB(0.25, 0, Math.max(0.03, Math.min(1, p)));
}

export const flat = <T extends THREE.Object3D>(m: T, y: number): T => { m.rotation.x = -Math.PI / 2; m.position.y = y; return m; };

/* Extra outputs for the one scene pass: location 0 stays the lit colour, then albedo, mask code, view normal. */
function gbuffer(mat: THREE.Material) {
  if (mat.userData.uMask) return mat;
  const uMask = mat.userData.uMask = { value: new THREE.Color() };
  const basic = (mat as any).isMeshBasicMaterial;  // unlit fx: no normal in the stock shader, so pass one through
  mat.onBeforeCompile = (s) => {
    s.uniforms.uMask = uMask;
    if (basic) {
      s.vertexShader = 'varying vec3 vGNrm;\n' + s.vertexShader.replace('#include <project_vertex>', `#include <project_vertex>
  vec3 gN = normal;
  #ifdef USE_INSTANCING
    gN = mat3(instanceMatrix) * gN;
  #endif
  vGNrm = normalMatrix * gN;`);
    }
    s.fragmentShader = `layout(location = 1) out highp vec4 gAlb;
layout(location = 2) out highp vec4 gMask;
layout(location = 3) out highp vec4 gNrm;
uniform vec3 uMask;
${basic ? 'varying vec3 vGNrm;' : ''}
` + s.fragmentShader.replace(/}\s*$/, `  gAlb = vec4(diffuseColor.rgb, 1.);
  gMask = vec4(uMask, 1.);
  gNrm = vec4(normalize(${basic ? 'vGNrm * (gl_FrontFacing ? 1. : -1.)' : 'normal'}) * .5 + .5, 1.);
}`);
  };
  return mat;
}
/* Copy each mesh's current mask code into the material it renders with. A mesh on a shared material whose
   mask no longer matches the shared one's (ghost, dying) switches to a private copy — made once, kept for
   the mesh's lifetime — and returns to the shared material once its mask matches again. */
export function syncMasks() {
  for (const m of meshes) {
    const want = m.userData.mats.mask.color, shared = m.userData.sharedLit as THREE.MeshLambertMaterial | undefined;
    if (shared) {
      if (shared.userData.uMask.value.equals(want)) { if (m.material !== shared) m.material = shared; continue; }
      m.material = m.userData.ownLit ??= gbuffer(new THREE.MeshLambertMaterial({ color: shared.color, side: shared.side }));
    }
    (m.material as THREE.Material).userData.uMask.value.copy(want);
  }
}

const NOISE = /* glsl */`
float hash(vec2 p){ p = fract(p*vec2(123.34, 456.21)); p += dot(p, p+45.32); return fract(p.x*p.y); }
float noise(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f);
  return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x), mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),f.x), f.y); }
float fbm(vec2 p){ float v=0., a=.5; for(int i=0;i<4;i++){ v+=a*noise(p); p*=2.03; a*=.5; } return v; }
`;

/* Paper grain depends only on the screen pixel: baked once per resize. */
const PAPER_FRAG = /* glsl */`
uniform float uDpr;
varying vec2 vUv;
${NOISE}
void main(){
  vec2 fc = gl_FragCoord.xy;
  gl_FragColor = vec4(vec3(.93,.87,.74)*(.94 + fbm(fc*.012/uDpr)*.08 + fbm(fc*vec2(.3,.02)/uDpr)*.04), 1.);
}`;

const FRAG = /* glsl */`
uniform sampler2D tAlb, tLit, tNrm, tDep, tMask, tPaper;
uniform vec2 uRes, uUvScale, uUvMax;
uniform float uDpr, uNear, uFar, uLightMax, uVisOn, uSat;
uniform sampler2D tVis;
uniform mat4 uInvProj, uCamWorld;
uniform vec2 uMapHalf;
varying vec2 vUv;

${NOISE}
float lum(vec3 c){ return dot(c, vec3(.299,.587,.114)); }
float linD(vec2 uv){ float z = texture2D(tDep, uv).x*2.-1.; return 2.*uNear*uFar/(uFar+uNear - z*(uFar-uNear)); }
vec3 nrm(vec2 uv){ return texture2D(tNrm, uv).xyz*2.-1.; }
// albedo, lit colour, normal and mask at the pixel itself are read once in main() and passed down. Depth is
// the exception: hoisting its fetch (worldAt's and linD's are the same texel; the compiler shares them)
// changes the GPU's instruction fusion and moves a handful of pixels by one level.
float lightAt(vec3 alb, vec3 lit){
  float a = lum(alb), l = lum(lit);
  return clamp(l/max(a,.03)/uLightMax, 0., 1.);
}
// uUvMax keeps the neighbour taps inside the rendered part of the G-buffer when it is drawn at reduced scale
float edges(vec2 uv, float w, float albW, float d0, vec3 n0, vec3 alb){
  vec2 t = w/uRes;
  float a0 = lum(alb);
  float ed=0., en=0., ea=0.;
  vec2 o[4]; o[0]=vec2(t.x,0.); o[1]=vec2(-t.x,0.); o[2]=vec2(0.,t.y); o[3]=vec2(0.,-t.y);
  for(int i=0;i<4;i++){
    vec2 q = min(uv+o[i], uUvMax);
    ed = max(ed, abs(linD(q)-d0)/d0);
    en = max(en, 1.-dot(n0, nrm(q)));
    ea = max(ea, abs(lum(texture2D(tAlb,q).rgb)-a0));
  }
  return clamp(smoothstep(.015,.04,ed) + smoothstep(.2,.5,en) + albW*smoothstep(.06,.16,ea), 0., 1.);
}
float hatch(vec2 fc, float ang, float sp, float t){
  float p = dot(fc, vec2(cos(ang), sin(ang)))/sp;
  float f = abs(fract(p)-.5)*2.;
  return 1.-smoothstep(t-.15, t, f);
}

vec3 worldAt(vec2 uv, float dep){
  vec4 v = uInvProj * vec4(uv*2.-1., dep*2.-1., 1.);
  return (uCamWorld * vec4(v.xyz/v.w, 1.)).xyz;
}

// soft rim of the team's sight: a small tent blur of the hard-edged vision polygons
float visAt(vec2 p){
  vec2 t = 1.5/vec2(textureSize(tVis, 0));  // four bilinear taps cover a 4×4 texel footprint
  return .25*(texture2D(tVis, p+vec2(t.x,t.y)).r + texture2D(tVis, p+vec2(-t.x,t.y)).r
            + texture2D(tVis, p+vec2(t.x,-t.y)).r + texture2D(tVis, p+vec2(-t.x,-t.y)).r);
}

float stipple(vec2 fc, float density){
  vec2 cell = fc/(3.5*uDpr);
  vec2 id = floor(cell), f = fract(cell)-.5;
  vec2 j = vec2(hash(id), hash(id+7.3))-.5;
  float r = .5*sqrt(density)*(.6+.4*hash(id+3.1));
  return 1.-smoothstep(r-.12, r, length(f - j*.45));
}

void main(){
  // vUv spans the screen; uv the part of the G-buffer this frame was drawn into (all of it at full scale)
  vec2 fc = gl_FragCoord.xy, uv = vUv*uUvScale;
  vec3 paper = texture2D(tPaper, vUv).rgb;

  vec3 a = texture2D(tAlb,uv).rgb;
  vec3 n0 = nrm(uv);
  vec4 m = texture2D(tMask,uv);
  float L = lightAt(a, texture2D(tLit,uv).rgb);
  float v = L*(.3+.7*lum(a)/.6);
  vec2 f2 = fc + n0.xy*8.*uDpr;
  float sp = 5.*uDpr;
  float ink = hatch(f2, .785, sp, clamp((.85-v)*1.1, 0., .9));
  ink = max(ink, hatch(f2, -.785, sp, clamp((.55-v)*1.3, 0., .9)));
  ink = max(ink, hatch(f2, 0., sp*.8, clamp((.3-v)*1.6, 0., .9)));
  // tree crowns: dense hatching over a whole grove flickers, so thin it out
  float foliage = step(.085, m.r) * step(m.r, .1) * step(max(abs(m.r-m.g), abs(m.g-m.b)), .02);
  ink *= 1. - .35*foliage;

  // dying hero: mask (0.25, 0, p) — colour drains away, then ink floods the body as p goes 0 → 1
  float dying = step(.2, m.r) * step(m.r, .3) * step(m.g, .05) * step(.02, m.b);
  vec3 tint = m.r>.5 ? vec3(.78,.32,.22) : vec3(.25,.37,.6);
  vec3 base = mix(paper, paper*tint*1.3, .6*max(m.r,m.b)*(1.-dying));
  // heroes: paper takes on the part's own color, like an ink wash under the hatching
  float unit = step(.1, m.r) * step(m.r, .5) * step(m.g, .5) * (1.-dying);
  base = mix(base, base*clamp(a*1.35, 0., 1.), .7*unit);
#ifdef FOG_COLOR
  float fogged = 0.;
#endif
  // fog of war: what the team sees takes colour; the rest stays a two-tone print
  if(uVisOn > .5){
    vec3 w = worldAt(vUv, texture2D(tDep, uv).x);
    float vis = visAt(vec2((w.x+uMapHalf.x)/(2.*uMapHalf.x), 1.-(w.z+uMapHalf.y)/(2.*uMapHalf.y)));
    float grey = step(max(abs(m.r-m.g), abs(m.g-m.b)), .02);
    float prop = step(.04, m.r) * step(m.r, .1) * grey, plain = step(m.r, .02) * grey;
    base = mix(base, base*clamp(a*1.35, 0., 1.), vis*(prop*.65 + plain*.18));
#ifdef FOG_COLOR
    fogged = (1.-vis)*(prop + plain);
#endif
  }
  vec3 inkC = vec3(.19,.12,.08);
#ifdef FOG_COLOR
  // fog variant: the fill stays paper, only strokes and contours keep the object's hue — muted, and brought
  // down to one ink-dark luminance, so pale ground and bright crowns hatch as heavily as plain ink does
  vec3 hue = mix(vec3(lum(a)), a, .7);
  vec3 strokeC = mix(inkC, hue*(.17/max(lum(hue), .05)), fogged);
#else
  #define strokeC inkC
#endif
  vec3 c = mix(base, strokeC, ink);
  // fx: stippled (dot engraving) over a wash of their own hue — part of the plate, yet never the wrong team color
  if(m.g>.5){
    float dens = .4 + .4*noise(fc*.05/uDpr);
    vec3 fxc = mix(mix(base, a, .55), a*.42, stipple(fc, dens));
    c = fxc;
  }
  float ghost = step(.3, m.b) * step(m.b, .7) * step(m.r, .05) * step(m.g, .05);
  if(ghost > .5) c = paper;
  // shade (0, .12, .5): paper crossed by sparse wide diagonal strokes — present, but only as a sketch
  float shade = step(.08, m.g) * step(m.g, .16) * step(m.r, .05) * step(.3, m.b) * step(m.b, .7);
  if(shade > .5) c = mix(paper, inkC, .6*hatch(fc, .785, sp*1.8, .3));
  if(dying > .5){
    float n = fbm(fc*.018/uDpr + 3.7) * .75 + noise(fc*.12/uDpr) * .25;
    float flood = smoothstep(n - .06, n + .02, m.b*1.15 - .1);
    c = mix(c, inkC*.85, flood);
  }
  float e = edges(uv, 1.4*uDpr, .7, linD(uv), n0, a);
  c = mix(c, strokeC, e);
#ifdef DESATURATE
  c = mix(vec3(lum(c)), c, uSat);
#endif
  gl_FragColor = vec4(c, 1.);
}`;

/* Toon look (settings "Art style", or ?style=toon): the same G-buffer composed as cel shading — banded light over a
   saturated albedo, cool night shadows, rim light on heroes, dark contours; fog of war is a dim desaturated
   night. Mask codes keep their meaning (fx, ghost, shade, dying); paper and FOG_COLOR play no part here. */
const TOON_FRAG = /* glsl */`
uniform sampler2D tAlb, tLit, tNrm, tDep, tMask;
uniform vec2 uRes, uUvScale, uUvMax;
uniform float uDpr, uNear, uFar, uLightMax, uVisOn, uSat;
uniform sampler2D tVis;
uniform mat4 uInvProj, uCamWorld;
uniform vec2 uMapHalf;
varying vec2 vUv;

${NOISE}
float lum(vec3 c){ return dot(c, vec3(.299,.587,.114)); }
float linD(vec2 uv){ float z = texture2D(tDep, uv).x*2.-1.; return 2.*uNear*uFar/(uFar+uNear - z*(uFar-uNear)); }
vec3 nrm(vec2 uv){ return texture2D(tNrm, uv).xyz*2.-1.; }
float lightAt(vec3 alb, vec3 lit){
  float a = lum(alb), l = lum(lit);
  return clamp(l/max(a,.03)/uLightMax, 0., 1.);
}
float edges(vec2 uv, float w, float d0, vec3 n0, vec4 m0){
  vec2 t = w/uRes;
  float ed=0., en=0., em=0.;
  vec2 o[4]; o[0]=vec2(t.x,0.); o[1]=vec2(-t.x,0.); o[2]=vec2(0.,t.y); o[3]=vec2(0.,-t.y);
  for(int i=0;i<4;i++){
    vec2 q = min(uv+o[i], uUvMax);
    // one-sided: only the pixel nearer the camera draws the line, so a contour is one tap wide, not two
    float dq = (linD(q)-d0)/d0;
    ed = max(ed, dq);
    en = max(en, 1.-dot(n0, nrm(q)));
    vec3 dm = abs(texture2D(tMask,q).rgb - m0.rgb);
    em = max(em, max(dm.r, max(dm.g, dm.b)) * step(-.002, dq));
  }
  // object boundaries (mask change) always outline; normal creases only lightly, so low-poly faces stay flat
  return clamp(smoothstep(.02,.05,ed) + .5*smoothstep(.35,.7,en) + step(.05, em), 0., 1.);
}
vec3 worldAt(vec2 uv, float dep){
  vec4 v = uInvProj * vec4(uv*2.-1., dep*2.-1., 1.);
  return (uCamWorld * vec4(v.xyz/v.w, 1.)).xyz;
}
float visAt(vec2 p){
  vec2 t = 1.5/vec2(textureSize(tVis, 0));
  return .25*(texture2D(tVis, p+vec2(t.x,t.y)).r + texture2D(tVis, p+vec2(-t.x,t.y)).r
            + texture2D(tVis, p+vec2(t.x,-t.y)).r + texture2D(tVis, p+vec2(-t.x,-t.y)).r);
}
vec3 saturate3(vec3 c, float k){ return clamp(mix(vec3(lum(c)), c, k), 0., 1.); }

void main(){
  vec2 fc = gl_FragCoord.xy, uv = vUv*uUvScale;
  vec3 a = texture2D(tAlb,uv).rgb;
  vec3 n0 = nrm(uv);
  vec4 m = texture2D(tMask,uv);
  float L = lightAt(a, texture2D(tLit,uv).rgb);

  float grey = step(max(abs(m.r-m.g), abs(m.g-m.b)), .02);
  float dying = step(.2, m.r) * step(m.r, .3) * step(m.g, .05) * step(.02, m.b);
  float unit = step(.1, m.r) * step(m.r, .5) * step(m.g, .5) * (1.-dying);
  float team = step(.5, max(m.r, m.b)) * step(m.g, .05);
  float hero = max(unit, team);

  // three bands: cast/self shadow, half-lit, full sun — ambient alone sits at ~.53 of uLightMax
  float lit = smoothstep(.6, .64, L)*.45 + smoothstep(.86, .9, L)*.55;
  // team paint is already saturated: boosting it again floods the whole hero into one flat blob
  vec3 col = saturate3(a, mix(1.45, 1.05, team)) * mix(1., 1.12, unit);
  vec3 shadowC = col * vec3(.48,.54,.72);
  vec3 midC = col * vec3(.76,.82,.94);
  vec3 sunC = col * vec3(1.08,1.02,.9);
  vec3 c = lit < .5 ? mix(shadowC, midC, lit/.45) : mix(midC, sunC, (lit-.45)/.55);
  // the world (ground, props) sits in a cool night; heroes keep their full colour so they pop
  c = mix(c * vec3(.5,.66,.74), c, hero);
  // rim light: silhouette edges of heroes catch a pale cold light
  float rim = pow(1. - clamp(n0.z, 0., 1.), 3.);
  c += hero * rim * vec3(.35,.45,.6) * .8;

  if(uVisOn > .5){
    vec3 w = worldAt(vUv, texture2D(tDep, uv).x);
    float vis = visAt(vec2((w.x+uMapHalf.x)/(2.*uMapHalf.x), 1.-(w.z+uMapHalf.y)/(2.*uMapHalf.y)));
    vec3 night = mix(vec3(lum(c)), c, .35) * vec3(.42,.52,.72) * .7;
    c = mix(night, c, vis);
  }
  vec3 lineC = mix(c * .22, vec3(.04,.06,.12), .5);
  // fx: a calm fill of their own hue with a bright rim of it, so skill areas don't drown the fight
  if(m.g > .5) { c = saturate3(a, 1.25) * .62; lineC = min(saturate3(a, 1.4) * 1.25 + .1, 1.); }
  float ghost = step(.3, m.b) * step(m.b, .7) * step(m.r, .05) * step(m.g, .05);
  if(ghost > .5) { c = vec3(.1,.16,.24); lineC = vec3(.75,.85,1.); }
  float shade = step(.08, m.g) * step(m.g, .16) * step(m.r, .05) * step(.3, m.b) * step(m.b, .7);
  if(shade > .5) c = mix(vec3(.1,.16,.24), vec3(.3,.4,.6), .5 + .5*sin((fc.x+fc.y)/(6.*uDpr)));
  if(dying > .5){
    c = mix(c, vec3(lum(c)) * vec3(.5,.55,.7), min(1., m.b*2.));
    float n = fbm(fc*.018/uDpr + 3.7) * .75 + noise(fc*.12/uDpr) * .25;
    c = mix(c, vec3(.05,.06,.1), smoothstep(n - .06, n + .02, m.b*1.15 - .1));
  }
  float e = edges(uv, 1.*uDpr, linD(uv), n0, m);
  c = mix(c, lineC, e);
  // soft vignette, like a spotlight on the hero
  vec2 vd = vUv - .5;
  c *= 1. - .35*smoothstep(.25, .75, length(vd*vec2(1., .8)));
#ifdef DESATURATE
  c = mix(vec3(lum(c)), c, uSat);
#endif
  gl_FragColor = vec4(c, 1.);
}`;

export type RenderStyle = 'engrave' | 'toon';

/* fogColor (default on; ?fog=mono gives the old two-tone fog) — outside vision the hatching and contours keep a
   muted object colour over paper, so "colour fill" reads as seen and "coloured strokes" as fog. */
export function createEngraver(canvas: HTMLCanvasElement, stageEl: HTMLElement, { fogColor = false, style = 'engrave' }: { fogColor?: boolean; style?: RenderStyle } = {}) {
  THREE.ColorManagement.enabled = false;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  (renderer as any).useLegacyLights = true;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.setClearColor(0x000000, 1);

  const scene = new THREE.Scene();
  // near plane well away from the lens: flat ground layers stay a few cm apart, so depth precision matters
  const camera = new THREE.PerspectiveCamera(36, 1, 4, 160);
  // shade is lit by ambient alone; the sum stays 1.14 so full sun keeps its tone and only shadows lighten
  const AMBIENT = 0.6, SUN = 0.54;
  scene.add(new THREE.AmbientLight(0xffffff, AMBIENT));
  const sun = new THREE.DirectionalLight(0xffffff, SUN);
  sun.position.set(-12, 22, 8);
  sun.castShadow = true;
  // shadow box follows the camera focus; wider than the view so its edge never shows, normal bias kills acne on flat ground
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, { left: -32, right: 32, top: 32, bottom: -32, near: 1, far: 90 });
  sun.shadow.bias = -0.0012;
  sun.shadow.normalBias = 0.06;
  scene.add(sun, sun.target);

  let rt: THREE.WebGLMultipleRenderTargets | null = null;
  const U = {
    tAlb: { value: null as THREE.Texture | null }, tPaper: { value: null as THREE.Texture | null }, tLit: { value: null as THREE.Texture | null },
    tNrm: { value: null as THREE.Texture | null }, tDep: { value: null as THREE.Texture | null }, tMask: { value: null as THREE.Texture | null },
    uRes: { value: new THREE.Vector2(1, 1) }, uDpr: { value: 1 },
    uUvScale: { value: new THREE.Vector2(1, 1) }, uUvMax: { value: new THREE.Vector2(1, 1) },
    uNear: { value: camera.near }, uFar: { value: camera.far }, uLightMax: { value: AMBIENT + SUN },
    uVisOn: { value: 0 }, uSat: { value: 1 }, tVis: { value: null as THREE.Texture | null }, uMapHalf: { value: new THREE.Vector2(1, 1) },
    uInvProj: { value: new THREE.Matrix4() }, uCamWorld: { value: new THREE.Matrix4() },
  };
  const postMat = new THREE.ShaderMaterial({
    uniforms: U,
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
    fragmentShader: style === 'toon' ? TOON_FRAG : FRAG,
    defines: fogColor ? { FOG_COLOR: '' } : {},
    depthTest: false, depthWrite: false,
  });
  const post = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), postMat);
  const postScene = new THREE.Scene();
  postScene.add(post);
  const postCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const paperMat = new THREE.ShaderMaterial({
    uniforms: { uDpr: U.uDpr },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
    fragmentShader: PAPER_FRAG,
    depthTest: false, depthWrite: false,
  });
  let paperRt: THREE.WebGLRenderTarget | null = null;
  function bakePaper(w: number, h: number) {
    if (paperRt) paperRt.dispose();
    paperRt = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    post.material = paperMat;
    renderer.setRenderTarget(paperRt);
    renderer.render(postScene, postCam);
    renderer.setRenderTarget(null);
    post.material = postMat;
    U.tPaper.value = paperRt.texture;
  }

  function makeTargets(w: number, h: number) {
    if (rt) { rt.depthTexture.dispose(); rt.dispose(); }
    rt = new THREE.WebGLMultipleRenderTargets(w, h, 4, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    rt.depthTexture = new THREE.DepthTexture(w, h);
    [U.tLit.value, U.tAlb.value, U.tMask.value, U.tNrm.value] = rt.texture as [THREE.Texture, THREE.Texture, THREE.Texture, THREE.Texture];
    U.tDep.value = rt.depthTexture;
    if (lowRt) { lowRt.dispose(); lowRt = null; }
  }

  /* Dynamic resolution without reallocation: every target stays at full (scale 1) size, a reduced frame is
     drawn into the corner of the G-buffer, composed into the same corner of `lowRt`, and stretched onto the
     canvas by one bilinear blit — what the browser did before when the canvas itself shrank. At scale 1 the
     post pass writes the canvas directly, exactly as without dynamic resolution. */
  let lowRt: THREE.WebGLRenderTarget | null = null;
  const blitMat = new THREE.ShaderMaterial({
    uniforms: { tSrc: { value: null as THREE.Texture | null }, uUvScale: U.uUvScale, uUvMax: U.uUvMax },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }',
    // clamped to the last drawn texel centre: bilinear taps past it would pick up the unused part of lowRt
    fragmentShader: 'uniform sampler2D tSrc; uniform vec2 uUvScale, uUvMax; varying vec2 vUv; void main(){ gl_FragColor = texture2D(tSrc, min(vUv*uUvScale, uUvMax)); }',
    depthTest: false, depthWrite: false,
  });
  let fullW = 1, fullH = 1, baseDpr = 1, renderScale = 1;
  function applyScale() {
    if (!rt) return;
    const w = renderScale < 1 ? Math.max(1, Math.floor(fullW * renderScale)) : fullW;
    const h = renderScale < 1 ? Math.max(1, Math.floor(fullH * renderScale)) : fullH;
    rt.viewport.set(0, 0, w, h);
    U.uDpr.value = baseDpr * renderScale;
    U.uUvScale.value.set(w / fullW, h / fullH);
    U.uUvMax.value.set((w - 0.5) / fullW, (h - 0.5) / fullH);
    if (renderScale < 1) {
      lowRt ??= new THREE.WebGLRenderTarget(fullW, fullH, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
      lowRt.viewport.set(0, 0, w, h);
      blitMat.uniforms.tSrc!.value = lowRt.texture;
    }
  }
  function resize() {
    const r = stageEl.getBoundingClientRect();
    if (!r.width || !r.height) return;
    baseDpr = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(baseDpr);
    renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / r.height;
    camera.updateProjectionMatrix();
    fullW = Math.floor(r.width * baseDpr); fullH = Math.floor(r.height * baseDpr);
    makeTargets(fullW, fullH);
    U.uRes.value.set(fullW, fullH);
    U.uDpr.value = baseDpr;
    bakePaper(fullW, fullH);
    applyScale();
  }
  new ResizeObserver(resize).observe(stageEl);
  resize();

  /* Vision texture: white = seen. Covers the map rectangle [-half.x, half.x] × [-half.z, half.z]. */
  function setVision(texture: THREE.Texture | null, halfX: number, halfZ: number) {
    U.tVis.value = texture;
    U.uMapHalf.value.set(halfX, halfZ);
    U.uVisOn.value = texture ? 1 : 0;
  }
  /* Whole-frame saturation, 1 = normal, 0 = black and white (the pre-match countdown, matchView.ts). The
     mix is compiled in only while below 1 (DESATURATE), so normal play pays nothing for it; the define
     flips — one recompile — only on a 1 ↔ below-1 transition, i.e. twice a match. */
  function setSaturation(k: number) {
    const v = Math.max(0, Math.min(1, k));
    U.uSat.value = v;
    if (('DESATURATE' in postMat.defines) === (v < 1)) return;
    if (v < 1) postMat.defines.DESATURATE = ''; else delete postMat.defines.DESATURATE;
    postMat.needsUpdate = true;
  }
  /* Shadows are sharp only near the camera focus: the sun follows it across a big map. */
  function followSun(x: number, z: number) {
    sun.position.set(x - 12, 22, z + 8);
    sun.target.position.set(x, 0, z);
    sun.target.updateMatrixWorld();
  }

  /* Per-pass draw calls for the profiler. */
  const stats = { calls: 0, tris: 0, passes: {} as Record<string, number> };
  function pass(name: string, fn: () => void) {
    renderer.info.reset();
    fn();
    stats.passes[name] = renderer.info.render.calls;
    stats.calls += renderer.info.render.calls;
    stats.tris += renderer.info.render.triangles;
  }
  function render() {
    if (!rt) return;
    stats.calls = 0; stats.tris = 0;
    renderer.info.autoReset = false;
    camera.updateMatrixWorld();
    U.uInvProj.value.copy(camera.projectionMatrixInverse);
    U.uCamWorld.value.copy(camera.matrixWorld);
    syncMasks();
    pass('scene+shadow', () => { renderer.shadowMap.needsUpdate = true; renderer.setRenderTarget(rt); renderer.render(scene, camera); });
    if (renderScale < 1 && lowRt) {
      pass('post', () => { renderer.setRenderTarget(lowRt); renderer.render(postScene, postCam); });
      pass('blit', () => {
        post.material = blitMat;
        renderer.setRenderTarget(null); renderer.render(postScene, postCam);
        post.material = postMat;
      });
    } else {
      delete stats.passes.blit;
      pass('post', () => { renderer.setRenderTarget(null); renderer.render(postScene, postCam); });
    }
  }
  /* Render resolution relative to the screen (1 = native). Cheap to change every few seconds: no reallocation. */
  function setRenderScale(k: number) { renderScale = Math.min(1, k); applyScale(); }
  /* Weak-GPU rung: a 1024² sun shadow map instead of 2048² (4× fewer texels to draw, softer edges). */
  function setShadowLow(low: boolean) {
    const size = low ? 1024 : 2048;
    if (sun.shadow.mapSize.x === size) return;
    sun.shadow.mapSize.set(size, size);
    sun.shadow.map?.dispose();
    (sun.shadow as any).map = null;
  }
  /* Swapping the post shader recompiles it once; applied at match start like the fog (matchView.ts). */
  function setStyle(next: RenderStyle) {
    const frag = next === 'toon' ? TOON_FRAG : FRAG;
    if (postMat.fragmentShader === frag) return;
    postMat.fragmentShader = frag;
    postMat.needsUpdate = true;
  }
  /* FOG_COLOR is a compile-time define: switching it recompiles the post shader once, at match start. */
  function setFogColor(on: boolean) {
    if (('FOG_COLOR' in postMat.defines) === on) return;
    postMat.defines = { ...(on ? { FOG_COLOR: '' } : {}), ...('DESATURATE' in postMat.defines ? { DESATURATE: '' } : {}) };
    postMat.needsUpdate = true;
  }
  return { renderer, scene, camera, render, setVision, followSun, setRenderScale, setShadowLow, setFogColor, setStyle, setSaturation, stats, uniforms: U };
}
